import { useState, useEffect, useRef, useCallback, useId } from 'react';
import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle, FormFeedback, Input, InputGroup } from 'reactstrap';
import { IOption } from '../../../types/common';
import { debounce } from '../../../utils/debounce';
import useClickOutside from '../../../hook/useClickOutside';
import Loading from '../loading';
import './typeahead.scss'

type TypeaheadProps = {
    id?: string;
    placeHolder: string;
    value?: IOption;
    options?: IOption[];
    dropdownOptions?: IOption[];
    delay?: number;
    loading: boolean;
    error?: string;
    query: (value: string) => void;
    onSelected: (option: IOption) => void;
    onClear?: () => void;
    onBlur?: () => void;
    disabled?:boolean
    onInput?: (value: string) => void;
    autoFocus?: boolean;
}

const Typeahead = (props: TypeaheadProps) => {
    const { id, placeHolder, value, options, dropdownOptions, delay, loading, error, query, onSelected, onClear, onBlur, disabled = false, onInput, autoFocus } = props

    const [queryStr, setQueryStr] = useState<string>('')
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
    const [buttonDropdownOpen, setButtonDropdownOpen] = useState<boolean>(false)
    //const [err, setErr] = useState<string>('')

    const wrapperRef = useRef<HTMLDivElement>(null)

    const tid = useId()
    const menuId = useId()

    const getId = () => id || tid

    const closeDropdown = useCallback(() => {
        setDropdownOpen(false)
        value && setQueryStr(value.label )
    }, [value])

    useClickOutside(wrapperRef, closeDropdown);

    useEffect(() => {
        if (options && options.length > 0 && queryStr.trim()) {
            setDropdownOpen(true)
        }
    }, [options])

    useEffect(() => {
        value && setQueryStr(value.label )
    }, [value])

    const onSearchStrChange = debounce(query, delay ?? 600)

    const onChangeInput = (event: any) => {
        onInput&&onInput(event.target.value.trim())
        if (event.target.value.trim()) {
            setQueryStr(event.target.value)
            onSearchStrChange(event.target.value.trim())
            //setErr('')
        } else {
            reset()
        }
    }

    const skipEscape = (event: any) => {
        if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
        }
    }

    const onInputKeyDown = (event: any) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
        } else if (dropdownOpen && event.key === 'ArrowDown') {
            event.preventDefault()
            event.stopPropagation()

            let children = event.target.closest('.input-container').querySelectorAll('.dropdown-item[tabIndex="-1"]')
            if (children && children.length > 0) {
                children[0].setAttribute('tabindex', '0')
                return children[0].focus()
            }
        } else if (dropdownOpen && event.key === 'Tab') {
            event.preventDefault()
            event.stopPropagation()
            setDropdownOpen(false)
        }
    }

    const onDdlKeyDown = (event: any, item: IOption) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            focusInput(event)
            setDropdownOpen(false)
        } else if (event.key === 'ArrowDown') {
            event.preventDefault()
            event.stopPropagation()

            let next = findNextSibling(event.target)
            if (next) {
                event.target.setAttribute('tabindex', '-1')
                next.setAttribute('tabindex', '0')
                next.focus()
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            event.stopPropagation()

            let prev = findPrevSibling(event.target)
            if (prev) {
                event.target.setAttribute('tabindex', '-1')
                prev.setAttribute('tabindex', '0')
                prev.focus()
            } else {
                event.target.setAttribute('tabindex', '-1')
                focusInput(event)
                setDropdownOpen(false)
            }
        } else if (event.key === 'Tab') {
            event.preventDefault()
            event.stopPropagation()

            focusInput(event)
            setDropdownOpen(false)
        } else if (event.key === 'Enter') {
            onSelect(item)
            focusInput(event)
        }
    }

    const focusInput = (event: any) => {
        event.target.closest('.input-container').querySelector(`#${getId()}`).focus()
    }

    const findNextSibling = (event: any) => {
        while (event.nextElementSibling) {
            if (event.nextElementSibling.className.indexOf('dropdown-item') > -1 && event.nextElementSibling.getAttribute("tabIndex") === '-1') {
                return event.nextElementSibling
            }
            event = event.nextElementSibling
        }
        return null
    }

    const findPrevSibling = (event: any) => {
        while (event.previousElementSibling) {
          if (event.previousElementSibling.className.indexOf('dropdown-item')>-1 && event.previousElementSibling.getAttribute("tabIndex")==='-1') {
              return event.previousElementSibling
          }
          event = event.previousElementSibling
        }
        return null
    }

    const reset = () => {
        setQueryStr('')
        setDropdownOpen(false)
        if (onClear) onClear()
    }

    const onSelect = (option: IOption) => {
        setDropdownOpen(false)
        onSelected(option)
    }

    const toggleButtonDropdown = () => {
        setButtonDropdownOpen(prevState => !prevState);
        setDropdownOpen(false)
    }

    const buttonDropdownSelecthandle = (option:IOption) => {
        setDropdownOpen(false)
        onSelected(option)
    }

    return (
        <div className="input-container" ref={wrapperRef}>
            <InputGroup>
            <Input type="search"
                id={getId()}
                placeholder={placeHolder}
                value={queryStr}
                title={queryStr}
                onChange={onChangeInput}
                onKeyDown={onInputKeyDown}
                onKeyUp={skipEscape}
                className="break-out"
                aria-controls={menuId}
                aria-haspopup="listbox"
                onBlur={onBlur}
                invalid={!!error}
                disabled={disabled}
                autoFocus={autoFocus}
            />
            {dropdownOptions &&
                <ButtonDropdown isOpen={buttonDropdownOpen} toggle={toggleButtonDropdown} disabled={disabled}>
                    <DropdownToggle caret/>
                    <DropdownMenu className='dropdownMenu'>
                        {dropdownOptions.map((option, index) => (
                        <DropdownItem key={index} onClick={() => buttonDropdownSelecthandle(option)}>
                            {option.label}
                        </DropdownItem>
                        ))}
                    </DropdownMenu>
                </ButtonDropdown>
            }
            {error && <FormFeedback role="alert">{error}</FormFeedback>}
            </InputGroup>
            {loading && <Loading style={{ float: 'right', margin: '-1.4em 3em 0 0' }} />}
            {dropdownOpen && (
                <div className="custom-dropdown" id={menuId} role="listbox">
                    {options?.map((item, index) => (
                        <div key={index}
                            tabIndex={-1}
                            className="dropdown-item break-out"
                            role="option"
                            onClick={() => onSelect(item)}
                            onKeyDown={event=>onDdlKeyDown(event, item)}
                            onKeyUp={skipEscape}
                            title={item.label}>
                            {item.displayItem ?? item.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default Typeahead
